/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axiosClient';
import { useNotifications } from '../../hooks/useNotifications';
import { useTheme } from '../../context/ThemeContext';
import moment from 'moment';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const Notifications = ({ shellConfig = null }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const pageTitle = shellConfig?.title || t('admin.notifications');
  const totalLabel = shellConfig?.totalLabel || t('admin.notification_singular');
  const backPath = shellConfig?.backPath || '/admin';
  const backLabel = shellConfig?.backLabel || t('admin.back_to_dashboard');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    read: 'all',
    search: '',
    from: '',
    to: ''
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0
  });

  const { 
    reload: reloadNotifications,
    deleteNotification,
    deleteNotificationsBulk
  } = useNotifications();

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(filters.read !== 'all' && { read: filters.read }),
        ...(filters.search && { q: filters.search }),
        ...(filters.from && { from: filters.from }),
        ...(filters.to && { to: filters.to })
      });

      const response = await axiosClient.get(`/notifications?${params}`);
      
      if (response.data.success) {
        setNotifications(response.data.data.items);
        setTotal(response.data.data.total);
        setHasMore(response.data.data.hasMore);
      } else {
        setError(t('admin.error_load_notifications'));
      }
    } catch (err) {
      setError(t('admin.error_load_notifications'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, t]);

  // Load notifications when filters or pagination change
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.type !== 'all') params.set('type', filters.type);
    if (filters.read !== 'all') params.set('read', filters.read);
    if (filters.search) params.set('q', filters.search);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (pagination.offset > 0) params.set('offset', pagination.offset);

    const newUrl = params.toString() ? `?${params.toString()}` : '';
    window.history.replaceState(null, '', window.location.pathname + newUrl);
  }, [filters, pagination]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
    setSelectedIds(new Set()); // Clear selections
  };

  // Handle pagination
  const handleNextPage = () => {
    if (hasMore) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  // Mark single notification as read
  const markAsRead = async (id) => {
    try {
      await axiosClient.patch(`/notifications/${id}/mark-read`);
      
      // Optimistic update
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      
      // Reload notification count in header
      reloadNotifications();
    } catch (err) {
      // Error handled silently
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axiosClient.patch('/notifications/mark-all-read');
      await loadNotifications();
      reloadNotifications();
    } catch (err) {
      // Error handled silently
    }
  };

  // Mark selected as read
  const markSelectedAsRead = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      await axiosClient.patch('/notifications/mark-read-bulk', {
        ids: Array.from(selectedIds)
      });
      
      setSelectedIds(new Set());
      await loadNotifications();
      reloadNotifications();
    } catch (err) {
      // Error handled silently
    }
  };

  // Handle delete selected notifications
  const deleteSelectedNotifications = async () => {
    if (selectedIds.size === 0) return;
    
    setDeleteData({
      title: t('admin.delete_selected_notifications'),
      message: t('admin.delete_selected_notifications_message', { count: selectedIds.size }),
      details: [
        `${t('admin.notifications_count_label')}: ${selectedIds.size}`,
        t('admin.delete_notifications_details')
      ],
      warning: t('admin.action_cannot_be_undone'),
      onConfirm: async () => {
        try {
          await deleteNotificationsBulk(Array.from(selectedIds));
          setSelectedIds(new Set());
          await loadNotifications();
          reloadNotifications();
        } catch (error) {
          setError(t('admin.error_delete_selected_notifications'));
        }
      }
    });
    setShowDeleteModal(true);
  };

  // Handle delete single notification
  const handleDeleteNotification = async (id) => {
    // Find the notification to get its details
    const notification = notifications.find(n => n.id === id);
    
    setDeleteData({
      title: t('admin.delete_notification'),
      message: t('admin.delete_notification_message'),
      details: [
        `${t('admin.type')}: ${notification ? formatType(notification.type) : t('admin.unknown')}`,
        `${t('admin.message')}: ${notification ? notification.message.substring(0, 50) + (notification.message.length > 50 ? '...' : '') : t('admin.unknown')}`,
        `${t('admin.col_created')}: ${notification ? moment(notification.created_at).format('MMM DD, YYYY HH:mm') : t('admin.unknown')}`
      ],
      warning: t('admin.action_cannot_be_undone'),
      onConfirm: async () => {
        try {
          await deleteNotification(id);
          await loadNotifications();
          reloadNotifications();
        } catch (error) {
          setError(t('admin.error_delete_notification'));
        }
      }
    });
    setShowDeleteModal(true);
  };

  // Handle row selection
  const handleRowSelect = (id, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked && notifications && notifications.length > 0) {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  // Get type pill color
  const getTypePillColor = (type) => {
    if (type.startsWith('booking.')) return 'bg-blue-500';
    if (type.startsWith('client.')) return 'bg-green-500';
    if (type.startsWith('barber.')) return 'bg-purple-500';
    if (type.startsWith('service.')) return 'bg-orange-500';
    if (type.startsWith('settings.')) return 'bg-gray-500';
    return 'bg-gray-500';
  };

  // Format type for display
  const formatType = (type) => {
    const keyMap = {
      'booking.created': 'admin.notification_type_booking_created',
      'booking.updated': 'admin.notification_type_booking_updated',
      'booking.cancelled': 'admin.notification_type_booking_cancelled',
      'booking.confirmed': 'admin.notification_type_booking_confirmed',
      'client.created': 'admin.notification_type_client_created',
      'client.updated': 'admin.notification_type_client_updated',
      'barber.created': 'admin.notification_type_barber_created',
      'barber.updated': 'admin.notification_type_barber_updated',
      'service.created': 'admin.notification_type_service_created',
      'service.updated': 'admin.notification_type_service_updated',
      'settings.updated': 'admin.notification_type_settings_updated'
    };

    if (keyMap[type]) {
      return t(keyMap[type]);
    }

    const prefixMap = {
      booking: t('admin.nav_bookings'),
      client: t('admin.nav_clients'),
      barber: t('admin.nav_barbers'),
      service: t('admin.nav_services'),
      settings: t('admin.settings')
    };

    const [prefix, suffix] = String(type || '').split('.');
    if (prefix && suffix && prefixMap[prefix]) {
      return `${prefixMap[prefix]} ${suffix}`;
    }

    return type.replace('.', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <div className="animate-pulse">
            <div className="h-8 rounded w-48 mb-2 bg-zinc-200 dark:bg-zinc-700"></div>
            <div className="h-4 rounded w-32 bg-zinc-200 dark:bg-zinc-700"></div>
          </div>
        </div>
        <Card>
          <div className="p-4 md:p-5">
            <div className="animate-pulse">
              <div className="h-6 rounded w-32 mb-4 bg-zinc-200 dark:bg-zinc-700"></div>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 rounded bg-zinc-200 dark:bg-zinc-700"></div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {pageTitle}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {total} {totalLabel}{total !== 1 ? 's' : ''}
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to={backPath}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {backLabel}
            </Link>
          </motion.div>
        </div>
      </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader
            title={t('admin.filters')}
            subtitle={t('admin.notifications_filters_subtitle')}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            }
          />
          <div className="p-4 md:p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.type')}
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="all">{t('admin.all')}</option>
                <option value="bookings">{t('admin.nav_bookings')}</option>
                <option value="clients">{t('admin.nav_clients')}</option>
                <option value="barbers">{t('admin.nav_barbers')}</option>
                <option value="services">{t('admin.nav_services')}</option>
                <option value="settings">{t('admin.settings')}</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.col_status')}
              </label>
              <select
                value={filters.read}
                onChange={(e) => handleFilterChange('read', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="all">{t('admin.all')}</option>
                <option value="unread">{t('admin.notifications_unread')}</option>
                <option value="read">{t('admin.notifications_read')}</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.from_date')}
              </label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => handleFilterChange('from', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.to_date')}
              </label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => handleFilterChange('to', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.search')}
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={t('admin.search_messages_placeholder')}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
            </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 flex-wrap">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={markAllAsRead}
                className="px-4 py-2 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg flex items-center gap-2"
                style={{ background: ACCENT }}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('admin.mark_all_as_read')}
              </motion.button>
              <motion.button
                whileHover={{ scale: selectedIds.size === 0 ? 1 : 1.05 }}
                whileTap={{ scale: selectedIds.size === 0 ? 1 : 0.95 }}
                onClick={markSelectedAsRead}
                disabled={selectedIds.size === 0}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${
                  selectedIds.size === 0
                    ? 'bg-zinc-400 cursor-not-allowed text-white'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-lg'
                }`}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                </svg>
                {t('admin.mark_selected_as_read', { count: selectedIds.size })}
              </motion.button>
              <motion.button
                whileHover={{ scale: selectedIds.size === 0 ? 1 : 1.05 }}
                whileTap={{ scale: selectedIds.size === 0 ? 1 : 0.95 }}
                onClick={deleteSelectedNotifications}
                disabled={selectedIds.size === 0}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${
                  selectedIds.size === 0
                    ? 'bg-zinc-400 cursor-not-allowed text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white hover:shadow-lg'
                }`}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('admin.delete_selected_count', { count: selectedIds.size })}
              </motion.button>
            </div>
          </div>
        </Card>

        {/* Notifications List */}
        {error && (
          <Card className="mb-6">
            <div className="p-4 md:p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-100">{t('admin.error')}</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {!notifications || notifications.length === 0 ? (
          <Card>
            <div className="p-12 text-center">
              <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                {t('admin.no_notifications_found')}
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {filters.type !== 'all' || filters.read !== 'all' || filters.search || filters.from || filters.to
                  ? t('admin.try_adjusting_filters')
                  : t('admin.you_are_all_caught_up')}
              </p>
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={t('admin.notifications')}
              subtitle={t('admin.total_notifications_count', { count: total })}
              icon={
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              }
              action={
                notifications && notifications.length > 0 && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === notifications.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-[--accent] rounded focus:ring-[--accent]/20 border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50"
                      style={{ '--accent': ACCENT }}
                    />
                    <span className="text-sm text-zinc-600 dark:text-zinc-400">{t('admin.select_all')}</span>
                  </div>
                )
              }
            />
            <div className="overflow-x-auto">
              <div className="divide-y divide-white/10 dark:divide-white/10">
                {notifications && notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    className="p-4 md:p-5 transition-colors cursor-pointer text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30"
                    onClick={() => !notification.read && markAsRead(notification.id)}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(notification.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRowSelect(notification.id, e.target.checked);
                        }}
                        className="mt-1 h-4 w-4 text-[--accent] rounded focus:ring-[--accent]/20 border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50"
                        style={{ '--accent': ACCENT }}
                      />
                      
                      {/* Unread Dot */}
                      {!notification.read && (
                        <div className="mt-2 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: ACCENT }}></div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              notification.type.startsWith('booking') ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                              notification.type.startsWith('client') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                              notification.type.startsWith('service') ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                              notification.type.startsWith('barber') ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                              'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
                            }`}>
                              {formatType(notification.type)}
                            </span>
                            <span className="text-sm text-zinc-500 dark:text-zinc-400">
                              {moment(notification.created_at).fromNow()}
                            </span>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title={t('admin.delete_notification')}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </motion.button>
                        </div>
                        
                        <p className="text-sm leading-relaxed text-zinc-900 dark:text-zinc-100">
                          {notification.message}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {notifications && notifications.length > 0 && (
          <Card className="mt-6">
            <div className="p-4 md:p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.showing_results_range', { from: pagination.offset + 1, to: Math.min(pagination.offset + pagination.limit, total), total, entity: t('admin.notifications').toLowerCase() })}
                </div>
                
                <div className="flex items-center gap-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrevPage}
                    disabled={pagination.offset === 0}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.previous')}
                  </motion.button>
                  <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {t('admin.page_of', { page: Math.floor(pagination.offset / pagination.limit) + 1, total: Math.ceil(total / pagination.limit) })}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNextPage}
                    disabled={!hasMore}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.next')}
                  </motion.button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deleteData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-6 rounded-2xl shadow-2xl max-w-md w-full mx-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
            >
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                  {deleteData.title}
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                  {deleteData.message}
                </p>
                {deleteData.details && (
                  <div className="bg-zinc-100 dark:bg-zinc-800/50 p-4 rounded-xl text-sm border border-white/10 dark:border-white/10">
                    {deleteData.details.map((detail, index) => (
                      <div key={index} className="text-zinc-700 dark:text-zinc-300">{detail}</div>
                    ))}
                  </div>
                )}
                {deleteData.warning && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                    {deleteData.warning}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  {t('admin.cancel')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowDeleteModal(false);
                    deleteData.onConfirm();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg"
                >
                  {t('admin.delete')}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
    </div>
  );
};

export default Notifications;